import React, { useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from './user-profile.styles';
import { useKeycloak } from '../../../features/authentication/keycloak/KeycloakProvider';
import AppConstants from '../../app.constants';
import { getRequest } from '../../utils/api';

interface CurrentUser {
  id?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

interface Props {
  readonly isCollapsed?: boolean;
}

const UserProfile = ({ isCollapsed }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const { tokenParsed } = keycloak;
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    if (!keycloak.isAuthenticated) return;

    const fetchUser = async (): Promise<void> => {
      try {
        const response = await getRequest<CurrentUser>('user', keycloak);
        if (response.json) {
          setUser(response.json);
        }
      } catch {
        // Silently fail — token info will be used as fallback
      }
    };

    fetchUser();
  }, [keycloak.isAuthenticated]);

  if (!tokenParsed && !user) {
    return null;
  }

  // Prefer DB values (from API), fall back to token claims
  const firstName = user?.firstName || (tokenParsed?.given_name as string | undefined) || '';
  const lastName = user?.lastName || (tokenParsed?.family_name as string | undefined) || '';
  const email = user?.email || (tokenParsed?.email as string | undefined) || '';
  const avatarUrl = user?.avatarUrl || '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ');

  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getRoleLabel = (): string | null => {
    if (keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE)) {
      return t('role-whyellow');
    }
    if (keycloak.hasRealmRole(AppConstants.ADMIN_ROLE)) {
      return t('role-administrator');
    }
    if (keycloak.hasRealmRole(AppConstants.OFFICEMANAGER_ROLE)) {
      return t('role-office-manager');
    }
    return null;
  };

  const roleLabel = getRoleLabel();

  return (
    <div className={style(styles.container(isCollapsed))}>
      <div className={style(styles.avatarWrapper)}>
        {avatarUrl ? (
          <img className={style(styles.avatarImage)} src={avatarUrl} alt={displayName || email} />
        ) : (
          <div className={style(styles.avatar)}>{getInitials(displayName || email)}</div>
        )}
        <span className={style(styles.statusDot)} />
      </div>
      {isCollapsed !== true && displayName && <div className={style(styles.name)}>{displayName}</div>}
      {isCollapsed !== true && email && <div className={style(styles.email)}>{email}</div>}
      {isCollapsed !== true && roleLabel && <span className={style(styles.roleBadge)}>{roleLabel}</span>}
    </div>
  );
};

export default UserProfile;
